<template>
  <!-- 프로비저닝 URL 1회 표시 — 생성 직후·[프로비저닝 URL 보기]·토큰 회전 셋이 같은 모달을 쓴다.
       URL 은 부모가 닫힘 뒤(after-close) 비운다: 컴포넌트 상태에 원문을 남기지 않는다. -->
  <a-modal
    :open="open"
    :footer="null"
    :mask-closable="false"
    :destroy-on-close="true"
    width="560px"
    :after-close="onAfterClose"
    @cancel="emit('close')"
  >
    <template #title>
      <div class="modal-title">{{ title }}</div>
    </template>

    <div class="modal-content">
      <div class="pbx-site-url">
        <p class="pbx-site-url__label">전화기 관리자 메뉴 Server Address 에 입력</p>
        <div class="pbx-site-url__value-row">
          <code class="pbx-site-url__value">{{ url }}</code>
          <button type="button" class="pbx-site-url__copy" @click="copy">
            <CopyOutlined />
            <span>복사</span>
          </button>
        </div>

        <!-- DHCP 160 은 폐기됐다 — 현장에서는 전화기 관리자 메뉴의 3필드만 넣는다 -->
        <dl class="pbx-site-url__guide">
          <div>
            <dt>Boot Server</dt>
            <dd>Static</dd>
          </div>
          <div>
            <dt>Server Type</dt>
            <dd>HTTPS</dd>
          </div>
          <div>
            <dt>Server Address</dt>
            <dd>{{ url }}</dd>
          </div>
        </dl>

        <p class="pbx-site-url__notice">{{ PBX_SITE_URL_NOTICE }}</p>
      </div>

      <div class="modal-footer">
        <a-button type="primary" class="yellow-button" @click="emit('close')">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { CopyOutlined } from '@ant-design/icons-vue';

import { copyPbxText } from '@/views/Callbot/service/usePbxAction';

/**
 * studio 는 URL 을 조립하지 않는다 — 서버가 준 provisioning_url 을 그대로 보인다(결정 11).
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 설치 장소"
 */
const PBX_SITE_URL_NOTICE =
  '이 창을 닫으면 URL 은 [프로비저닝 URL 보기]로만 다시 볼 수 있고, 그 열람은 감사 로그에 남습니다.';

const props = defineProps({
  open: { type: Boolean, required: true },
  title: { type: String, required: true },
  url: { type: String, required: true },
});

const emit = defineEmits(['close', 'afterClose']);

// antdv 4 의 afterClose 는 이벤트가 아니라 prop 이다 — 닫힘 애니메이션이 끝난 뒤 부모가 원문을 비운다
function onAfterClose() {
  emit('afterClose');
}

function copy() {
  if (props.url) void copyPbxText(props.url);
}
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 18px;
  font-weight: 700;
}

// 전역 Bootstrap 의 .modal-content 규칙(테두리·둥근 모서리·flex)을 끄고 여백을 준다
.modal-content {
  display: block;
  padding: 4px 24px 0;
  border: none;
  border-radius: 0;
  background: transparent;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.pbx-site-url {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;
  color: #262626;

  &__label {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }

  &__value-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
    margin-top: -6px;
  }

  &__value {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fafafa;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    word-break: break-all;
    user-select: all;
  }

  &__copy {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #1668dc;
    border-radius: 8px;
    background: #fff;
    color: #1668dc;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #f0f5ff;
    }
  }

  &__guide {
    margin: 0;
    padding: 12px 14px;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 6px;

    div {
      display: flex;
      gap: 8px;
      min-width: 0;
    }

    dt {
      flex: none;
      width: 110px;
      color: #8c8c8c;
      font-weight: 400;

      &::after {
        content: ' =';
      }
    }

    dd {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      word-break: break-all;
    }
  }

  &__notice {
    margin: 0;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.6;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
